import { metaKeyInBrowser } from '@bonsai-components/meta-key';
import { ModCodeSnippet } from '@moderneinc/design-system-components';
import {
  semanticColors,
  semanticTypography,
  typography
} from '@moderneinc/design-system-tokens';
import { Stack, type SxProps, type Theme, Typography } from '@mui/material';
import React, { type FunctionComponent } from 'react';

import {
  type CommandWithKeyBinding,
  isCommandWithKeyBindingWithKey
} from '../../contexts/command-palette.context';

type KeyboardShortcutVariant = 'contained' | 'outlined';

type KeyboardShortcutsProps = {
  shortcuts: CommandWithKeyBinding | CommandWithKeyBinding[];
  fontSize?: string;
  variant?: KeyboardShortcutVariant;
  sx?: SxProps<Theme>;
};

export const KeyboardShortcuts: FunctionComponent<KeyboardShortcutsProps> = ({
  shortcuts,
  fontSize,
  variant = 'contained',
  sx
}) => {
  return Array.isArray(shortcuts) ? (
    shortcuts
      .map((shortcut, index) => (
        <KeyboardShortcut
          key={index}
          shortcut={shortcut}
          fontSize={fontSize}
          variant={variant}
          sx={sx}
        />
      ))
      .reduce(
        (acc, kbd) =>
          acc === null ? (
            kbd
          ) : (
            <React.Fragment>
              {acc} or {kbd}
            </React.Fragment>
          ),
        null
      )
  ) : (
    <KeyboardShortcut
      shortcut={shortcuts}
      fontSize={fontSize}
      variant={variant}
      sx={sx}
    />
  );
};

type ShortcutKeyProps = {
  children: React.ReactNode;
  fontSize?: string;
  variant?: KeyboardShortcutVariant;
};

export const ShortcutKey: FunctionComponent<ShortcutKeyProps> = ({
  children,
  fontSize,
  variant = 'contained'
}) => (
  <ModCodeSnippet
    component="span"
    variant="outlined"
    size="small"
    tabIndex={-1}
    aria-hidden
    sx={{
      ...(fontSize ? { fontSize } : {}),
      ...(variant === 'outlined'
        ? {
            // ModCodeSnippet draws no border in either of its variants, so the
            // key-cap outline is ours; border.strong is the only border token
            // that reads against both the cream and the dark card.
            backgroundColor: 'transparent',
            border: '1px solid',
            borderColor: semanticColors.color.border.strong
          }
        : {})
    }}
  >
    {children}
  </ModCodeSnippet>
);

type KeyboardShortcutProps = {
  shortcut: CommandWithKeyBinding;
  fontSize?: string;
  variant?: KeyboardShortcutVariant;
  sx?: SxProps<Theme>;
};

export const KeyboardShortcut: FunctionComponent<KeyboardShortcutProps> = ({
  shortcut,
  fontSize,
  variant = 'contained',
  sx
}) => {
  return (
    <Stack
      direction="row"
      sx={[
        {
          gap: 0.5,
          alignItems: 'center'
        },
        ...(Array.isArray(sx) ? sx : [sx])
      ]}
    >
      {shortcut?.modifier?.meta && (
        <ShortcutKey fontSize={fontSize} variant={variant}>
          {metaKeyInBrowser({ windows: 'ctrl' })}
        </ShortcutKey>
      )}
      {shortcut?.modifier?.shift && (
        <ShortcutKey fontSize={fontSize} variant={variant}>
          shift
        </ShortcutKey>
      )}
      {shortcut?.modifier?.alt && (
        <ShortcutKey fontSize={fontSize} variant={variant}>
          alt
        </ShortcutKey>
      )}
      {shortcut?.modifier?.ctrl && (
        <ShortcutKey fontSize={fontSize} variant={variant}>
          ctrl
        </ShortcutKey>
      )}
      {isCommandWithKeyBindingWithKey(shortcut) ? (
        shortcut.key === '˚' ? (
          <React.Fragment>
            <ShortcutKey fontSize={fontSize} variant={variant}>
              alt
            </ShortcutKey>
            <ShortcutKey fontSize={fontSize} variant={variant}>
              k
            </ShortcutKey>
          </React.Fragment>
        ) : (
          <ShortcutKey fontSize={fontSize} variant={variant}>
            {shortcut.key}
          </ShortcutKey>
        )
      ) : (
        <ShortcutKey fontSize={fontSize} variant={variant}>
          {shortcut.code.replace('Key', '')}
        </ShortcutKey>
      )}
    </Stack>
  );
};

type KeyHintProps = {
  keys: string[];
  label: string;
};

export const KeyHint: FunctionComponent<KeyHintProps> = ({ keys, label }) => {
  return (
    <Stack
      direction="row"
      sx={{
        gap: 0.5,
        alignItems: 'center'
      }}
    >
      {keys.map((key) => (
        // 11px key caps to match the modal-footer mock; not a design-system token.
        <ShortcutKey key={key} fontSize="11px">
          {key}
        </ShortcutKey>
      ))}
      <Typography
        sx={{
          ...semanticTypography.body.small.regular,
          // Footer hint text is 13px in the mock, not the 14px body-small.
          fontSize: `${typography.fontSize.caption}px`,
          color: 'text.secondary'
        }}
      >
        {label}
      </Typography>
    </Stack>
  );
};
